import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import JiraKeyChip from '../JiraKeyChip';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { ReportLine, reportDay, timelineText } from './types';

const LINK = 'rounded px-1 py-1 text-xs font-medium text-blue-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

interface Props {
  line: ReportLine;
  /** The report changed (a remark saved, a line left out): read it again. */
  onChanged: () => void;
}

// One line of the report, as the sheet writes it: the project and feature, what it is, and where it stands by date. A line
// that is a Jira ticket links to its row in the Plan and to Jira, can carry a hand-written remark, and can be left out.
export default function ReportLineRow({ line, onChanged }: Props) {
  const uid = useId();
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [remark, setRemark] = useState(line.remark ?? '');
  const [busy, setBusy] = useState(false);

  const save = async (patch: Record<string, unknown>, done: string) => {
    if (!line.key) return;
    setBusy(true);
    try {
      const r = await apiService.updatePlanNote(line.key, patch);
      if (!r.success) throw new Error(r.error);
      showToast('success', done);
      setEditing(false);
      onChanged();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="align-top">
      <th scope="row" className="border-b border-gray-100 px-4 py-3 text-left font-normal sm:min-w-[14rem]">
        <span className="block break-words font-semibold leading-snug text-gray-900">
          {line.project_name} | {line.feature}
        </span>
      </th>
      <td className="border-b border-gray-100 px-3 py-3 text-gray-800">
        {line.description && <p className="break-words">{line.description}</p>}
        {line.remark && !editing && <p className="mt-1 break-words italic text-gray-700">*{line.remark}</p>}
        {editing && (
          <div className="mt-2">
            <label htmlFor={`${uid}-remark`} className="text-xs font-semibold text-gray-700">Remark for the report</label>
            <textarea id={`${uid}-remark`} rows={2} maxLength={2000} value={remark} onChange={(e) => setRemark(e.target.value)} className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
            <div className="mt-1 flex gap-2">
              <button type="button" disabled={busy} onClick={() => save({ report_remark: remark.trim() === '' ? null : remark }, 'Remark saved')} className="rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50">Save remark</button>
              <button type="button" onClick={() => { setEditing(false); setRemark(line.remark ?? ''); }} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">Cancel</button>
            </div>
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 print:hidden">
          {line.links.map((k) => (
            <JiraKeyChip key={k.key} issueKey={k.key} url={k.url} />
          ))}
          {line.key && (
            <>
              <Link to={`/plan?open=${encodeURIComponent(line.key)}`} className={LINK} aria-label={`Open ${line.feature} in the plan`}>Open in the plan</Link>
              <button type="button" onClick={() => setEditing(true)} className={LINK} aria-label={`${line.remark ? 'Edit the remark on' : 'Add a remark to'} ${line.feature}`}>{line.remark ? 'Edit remark' : 'Add a remark'}</button>
              <button type="button" disabled={busy} onClick={() => save({ hide_in_report: true }, 'Left out of the report')} className={LINK} aria-label={`Leave ${line.feature} out of the report`}>Leave out</button>
            </>
          )}
        </div>
      </td>
      <td className="border-b border-gray-100 px-3 py-3 font-semibold text-gray-900 sm:min-w-[11rem]">
        {line.section === 'planned' ? (
          <span>{line.planned_start ?? 'TBD'}</span>
        ) : line.timeline.length === 0 ? (
          <span aria-hidden>–</span>
        ) : (
          <ol aria-label={timelineText(line.timeline)} className="space-y-0.5">
            {line.timeline.map((t, i) => (
              <li key={`${t.date}${t.phase}${t.platform ?? ''}${i}`} className="tabular-nums">
                <span className="text-gray-700">{reportDay(t.date)}</span> {t.phase}
                {t.platform ? <span className="font-normal text-gray-700"> ({t.platform})</span> : null}
              </li>
            ))}
          </ol>
        )}
      </td>
    </tr>
  );
}
